// Editor för ImageBlock — src, alt-text, valfri bredd och länk.
// "Ladda upp"-knappen postar filen till /Image-endpointen och sätter src
// till den returnerade URL:en. "Välj från mediabibliotek" öppnar en modal
// med användarens tidigare uppladdade bilder.

import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import UploadIcon from '@mui/icons-material/CloudUpload';
import { Button, Checkbox, FormControlLabel, Stack, TextField } from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { createImage, ImageListItem } from 'src/shared/services/imageService';
import { MediaLibraryModal } from '../../components/mediaLibraryModal';
import { ImageBlock } from '../types';

type Props = {
  block: ImageBlock;
  onChange: (patch: Partial<ImageBlock>) => void;
};

export const ImageBlockEditor = ({ block, onChange }: Props) => {
  const { t } = useTranslation('groupMessage');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);

  const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Återställ input så samma fil kan väljas igen efter ett misslyckat upload-försök.
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const response = await createImage(file);
      const url = response.data?.location;
      if (typeof url === 'string' && url.length > 0) {
        onChange({ src: url, alt: block.alt || file.name });
      } else {
        toast.warning(t('builder.editors.uploadFailed') as string);
      }
    } catch {
      toast.warning(t('builder.editors.uploadFailed') as string);
    } finally {
      setUploading(false);
    }
  };

  const handleMediaLibrarySelect = (image: ImageListItem) => {
    onChange({ src: image.location, alt: block.alt || image.fileName });
  };

  return (
    <Stack spacing={2}>
      <TextField
        label={t('builder.editors.imageUrl') as string}
        value={block.src}
        onChange={(e) => onChange({ src: e.target.value })}
        fullWidth
        size='small'
        helperText={t('builder.editors.imageUrlHelper')}
      />
      <Stack direction='row' spacing={1}>
        <Button
          variant='outlined'
          startIcon={<UploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          size='small'
          sx={{ flex: 1 }}
        >
          {uploading ? t('builder.editors.uploading') : t('builder.editors.uploadFile')}
        </Button>
        <Button variant='outlined' startIcon={<PhotoLibraryIcon />} onClick={() => setLibraryOpen(true)} size='small' sx={{ flex: 1 }}>
          {t('builder.editors.openMediaLibrary')}
        </Button>
      </Stack>
      <input ref={fileInputRef} type='file' accept='image/*' onChange={handleFileChosen} style={{ display: 'none' }} />
      <TextField
        label={t('builder.editors.altText') as string}
        value={block.alt}
        onChange={(e) => onChange({ alt: e.target.value })}
        fullWidth
        size='small'
        helperText={t('builder.editors.altTextHelper')}
      />
      <TextField
        label={t('builder.editors.width') as string}
        type='number'
        value={block.width ?? ''}
        onChange={(e) => {
          const v = e.target.value;
          onChange({ width: v === '' ? undefined : Number(v) });
        }}
        fullWidth
        size='small'
        inputProps={{ min: 50, max: 600 }}
        helperText={t('builder.editors.widthHelper')}
      />
      <FormControlLabel
        control={
          <Checkbox checked={block.fullBleed ?? false} onChange={(e) => onChange({ fullBleed: e.target.checked || undefined })} size='small' />
        }
        label={t('builder.editors.fullBleed') as string}
      />
      <TextField
        label={t('builder.editors.linkOptional') as string}
        value={block.href ?? ''}
        onChange={(e) => {
          const v = e.target.value.trim();
          onChange({ href: v === '' ? undefined : v });
        }}
        fullWidth
        size='small'
        placeholder='https://...'
      />
      <MediaLibraryModal open={libraryOpen} onClose={() => setLibraryOpen(false)} onSelect={handleMediaLibrarySelect} />
    </Stack>
  );
};

export default ImageBlockEditor;
